import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EmptyStateComponent } from "src/app/_core/components/empty-state/empty-state.component";
import { AuditoriaEvento, AuditoriaOperacion } from "src/app/_core/models/auditoria.model";
import { AuditoriaService } from "src/app/_core/providers/auditoria.service";

/**
 * Pestaña "Historial" (audit trail). Carga el registro de cambios de la entidad y lo
 * presenta como una línea de tiempo SOLO LECTURA con un lenguaje visual informativo
 * (calmado, distinto de las tarjetas de edición). Standalone: se enchufa en el detalle
 * de contrato y de centro. La visibilidad de la pestaña la decide el padre (solo con
 * permiso de edición y fuera del modo edición); el back además gatea el endpoint.
 */
@Component({
	selector: "app-historial",
	standalone: true,
	imports: [CommonModule, MatIconModule, TranslateModule, EmptyStateComponent],
	templateUrl: "./historial.component.html",
	styleUrls: ["./historial.component.scss"],
})
export class HistorialComponent implements OnChanges {
	@Input() entidadTipo: "Contrato" | "Centro" = "Contrato";
	@Input() entidadId?: number;

	public eventos: AuditoriaEvento[] = [];
	public loading = false;

	/** Umbral para considerar un "salto" temporal notable entre eventos consecutivos (60 días). */
	private readonly UMBRAL_GAP_MS = 60 * 24 * 60 * 60 * 1000;

	constructor(
		private readonly auditoriaService: AuditoriaService,
		private readonly translate: TranslateService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["entidadId"] || changes["entidadTipo"]) {
			this.cargar();
		}
	}

	private cargar(): void {
		if (!this.entidadId || this.entidadId <= 0) {
			this.eventos = [];
			return;
		}

		this.loading = true;
		const peticion =
			this.entidadTipo === "Centro"
				? this.auditoriaService.getHistorialCentro(this.entidadId)
				: this.auditoriaService.getHistorialContrato(this.entidadId);

		peticion.subscribe({
			next: (eventos: AuditoriaEvento[]) => {
				this.eventos = eventos ?? [];
				this.loading = false;
			},
			error: () => {
				this.eventos = [];
				this.loading = false;
			},
		});
	}

	public iconoOperacion(op: AuditoriaOperacion): string {
		if (op === "Alta") return "add_circle";
		if (op === "Baja") return "cancel";
		return "edit";
	}

	public claseOperacion(op: AuditoriaOperacion): string {
		if (op === "Alta") return "is-alta";
		if (op === "Baja") return "is-baja";
		return "is-mod";
	}

	/** Valor legible: null/vacío → guion; booleano → Sí/No; fecha ISO → dd/MM/yyyy. */
	public mostrar(valor: string | null): string {
		if (!valor || valor.length === 0) return "—";
		if (valor === "True") return this.translate.instant("common.yes");
		if (valor === "False") return this.translate.instant("common.no");
		const fecha = /^(\d{4})-(\d{2})-(\d{2})T/.exec(valor);
		if (fecha) return `${fecha[3]}/${fecha[2]}/${fecha[1]}`;
		return valor;
	}

	/** Etiqueta legible del campo (i18n `historial.campos.{Campo}`); si no hay clave, el nombre técnico. */
	public etiquetaCampo(campo: string): string {
		const clave = `historial.campos.${campo}`;
		const traducido = this.translate.instant(clave);
		return traducido === clave ? campo : traducido;
	}

	public trackEvento(_: number, ev: AuditoriaEvento): string {
		return ev.correlacionId;
	}

	/** Tiempo transcurrido en lenguaje natural ("ahora mismo", "hace 3 días", "hace 2 años"). */
	public tiempoRelativo(fecha: string): string {
		const t = new Date(fecha).getTime();
		if (isNaN(t)) return "";
		const seg = Math.max(0, Math.floor((Date.now() - t) / 1000));
		if (seg < 60) return this.translate.instant("historial.rel.now");
		const min = Math.floor(seg / 60);
		if (min < 60) return this.frase("ago", min, "minute");
		const horas = Math.floor(min / 60);
		if (horas < 24) return this.frase("ago", horas, "hour");
		const dias = Math.floor(horas / 24);
		if (dias < 30) return this.frase("ago", dias, "day");
		const meses = Math.floor(dias / 30);
		if (meses < 12) return this.frase("ago", meses, "month");
		return this.frase("ago", Math.floor(dias / 365), "year");
	}

	/** ¿Hay un salto temporal notable entre este evento y el siguiente (más antiguo)? */
	public hayGapGrande(i: number): boolean {
		const ms = this.deltaMs(i);
		return ms != null && ms > this.UMBRAL_GAP_MS;
	}

	/** Texto aproximado del salto temporal entre el evento i y el siguiente ("≈ 2 años"). */
	public gapTexto(i: number): string {
		const ms = this.deltaMs(i);
		if (ms == null) return "";
		const dias = Math.floor(ms / (24 * 60 * 60 * 1000));
		if (dias >= 365) return this.frase("approx", Math.floor(dias / 365), "year");
		if (dias >= 30) return this.frase("approx", Math.floor(dias / 30), "month");
		return this.frase("approx", Math.max(1, dias), "day");
	}

	private deltaMs(i: number): number | null {
		if (i + 1 >= this.eventos.length) return null;
		const a = new Date(this.eventos[i].fecha).getTime();
		const b = new Date(this.eventos[i + 1].fecha).getTime();
		if (isNaN(a) || isNaN(b)) return null;
		return Math.abs(a - b);
	}

	private frase(modo: "ago" | "approx", n: number, unidad: string): string {
		const claveUnidad = `historial.rel.unit.${unidad}${n === 1 ? "" : "s"}`;
		const unit = this.translate.instant(claveUnidad);
		return this.translate.instant(`historial.rel.${modo}`, { n, unit });
	}
}
